import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import OnboardingLayout from '@/components/OnboardingLayout';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapPin, Globe, Phone, Users, Edit, Info } from 'lucide-react';
import { useGetCompanyData, useCompleteOnboarding } from '@/hooks/use-company';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { toast } from '@/hooks/use-toast';

export default function Step5ReviewFinish() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('orgId');
  // ...existing code...
  const { handleError } = useErrorHandler();

  // Map role names to design-system token classes. Returns combined
  // Tailwind utilities for the badge's text + background.
  const getRoleClasses = (roleName: string): string => {
    if (roleName === 'Sales Manager' || roleName === 'Project Manager') {
      return 'bg-secondary/10 text-secondary';
    }
    if (roleName === 'Sales Representative') {
      return 'bg-accent/10 text-accent';
    }
    return 'bg-muted text-muted-foreground';
  };

  // Fetch company profile and team members data
  const { data: companyData, isLoading: isLoadingCompanyData } =
    useGetCompanyData(organizationId, 'profile,teamMembers');

  // Complete onboarding mutation
  const completeOnboarding = useCompleteOnboarding();

  const handleComplete = async () => {
    if (!organizationId) {
      toast({
        title: 'Error',
        description: 'Organization ID is missing. Please try again.',
        variant: 'destructive',
      });
      return false; // Prevent navigation
    }

    try {
      const response = await completeOnboarding.mutateAsync(organizationId);

      toast({
        title: 'Onboarding Completed!',
        description: 'Your setup has been completed successfully.',
        variant: 'success',
      });

      // Navigate to the success page with tokens in state
      navigate(
        `/onboarding/success${organizationId ? `?orgId=${organizationId}` : ''}`,
        {
          state: {
            tokens: {
              access_token: response.access_token,
              refresh_token: response.refresh_token,
            },
            user: response.user,
          },
        }
      );
      return true;
    } catch (error: any) {
      handleError(error, {
        customMessage: 'Failed to complete onboarding. Please try again.',
      });
      return false;
    }
  };

  const handleEditTeamMembers = () => {
    navigate(
      `/onboarding/teamMembers${organizationId ? `?orgId=${organizationId}` : ''}`
    );
  };

  // Check for missing organizationId
  useEffect(() => {
    if (!organizationId) {
      toast({
        title: 'Missing Organization ID',
        description: 'Please complete the signup process first.',
        variant: 'warning',
      });
    }
  }, [organizationId]);

  return (
    <TooltipProvider>
      <OnboardingLayout
        currentStep={5}
        totalSteps={5}
        onNext={handleComplete}
        isLoading={completeOnboarding.isPending}
        saveButtonText="Complete Setup"
      >
        {/* Review Section */}
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-foreground mb-2">
              Review & Finish
            </h2>
            <p className="text-muted-foreground">
              Double-check your entries before submitting. Make sure all details
              are accurate and complete to finalize your setup.
            </p>
          </div>

          {/* Show loading state while fetching data */}
          {isLoadingCompanyData && (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-outline-variant border-t-slate-600 rounded-full animate-spin" />
              <span className="ml-2 text-muted-foreground">
                Loading company data...
              </span>
            </div>
          )}

          {/* Company Profile Section */}
          <Card className="border border-border">
            <CardHeader>
              <h3 className="font-newsreader text-xl font-semibold tracking-tight tracking-tight text-foreground">
                Company Profile
              </h3>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Phone Number */}
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-muted-foreground mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground">
                        {companyData?.profile?.phone || 'Not provided'}
                      </p>
                      <Tooltip delayDuration={100}>
                        <TooltipTrigger>
                          <Info className="w-4 h-4 text-muted-foreground/70 hover:text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-sm">
                            This is your company's main contact number used for
                            client communication and official inquiries.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Primary Contact Number
                    </p>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground mt-1" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {companyData?.profile
                        ? `${companyData.profile.address}, ${companyData.profile.city}, ${companyData.profile.state} ${companyData.profile.zipCode}`
                        : 'Not provided'}
                    </p>
                    <p className="text-sm text-muted-foreground">Location</p>
                  </div>
                </div>

                {/* Timezone */}
                <div className="flex items-start gap-3">
                  <Globe className="w-5 h-5 text-muted-foreground mt-1" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {companyData?.profile?.timezone || 'Not provided'}
                    </p>
                    <p className="text-sm text-muted-foreground">Timezone</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Team Members Section */}
          <Card className="border border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-muted-foreground" />
                  <h3 className="font-newsreader text-xl font-semibold tracking-tight tracking-tight text-foreground">
                    Team Members
                  </h3>
                </div>
                <Button
                  onClick={handleEditTeamMembers}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1 text-sm px-4 py-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Team Members Cards */}
              <div className="space-y-3">
                {companyData?.teamMembers &&
                  companyData.teamMembers.length > 0 ? (
                  companyData.teamMembers.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-3 border border-border rounded-lg bg-[hsl(var(--surface-container-low))]"
                    >
                      <div className="flex items-center gap-3">
                        {/* User Avatar with Initials */}
                        <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                          <span className="text-primary-foreground text-sm font-medium">
                            {member.fullName
                              .split(' ')
                              .map((name) => name[0])
                              .join('')
                              .toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {member.fullName}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {member.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {member.roles.map((userRole) => (
                          <Badge
                            key={userRole.id}
                            variant="secondary"
                            className={`text-xs border-0 ${getRoleClasses(userRole.role.name)}`}
                          >
                            {userRole.role.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-center py-4">
                    No team members added
                  </p>
                )}
              </div>

              {/* Next Steps Alert */}
              <Alert className="border-blue-200 bg-blue-50">
                <AlertDescription className="text-blue-800">
                  <strong>Next Steps:</strong> All team members will receive
                  email invitations to complete their account setup once you
                  finish this wizard.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </div>
      </OnboardingLayout>
    </TooltipProvider>
  );
}
